import { HttpError, useCustomMutation, useDataProvider } from "@refinedev/core";
import { useCallback, useState } from "react";
import {
  AddProductToSubscriptionRequest,
  OrganizationSubscription,
} from "@/types";

export function useManageSubscription() {
  const dataProvider = useDataProvider();
  const { mutateAsync, isPending: isSubmitting } =
    useCustomMutation<OrganizationSubscription>();
  const [isLoading, setIsLoading] = useState(false);

  // Returns the active subscription, or null when the org has none (API 404).
  const lookupSubscription = useCallback(
    async (
      organizationId: string
    ): Promise<OrganizationSubscription | null> => {
      setIsLoading(true);
      try {
        const result = await dataProvider().custom?.({
          url: `api/organizations/${organizationId}/subscriptions/active`,
          method: "get",
        });
        return result?.data as OrganizationSubscription | null;
      } catch (error) {
        // Only a 404 means "this org has no active subscription". Any other
        // failure (network, auth, 5xx) must be surfaced — otherwise it would be
        // misread as "no subscription" and drop the page into create mode for an
        // org that may actually hold a paid plan.
        if ((error as HttpError | null)?.statusCode === 404) {
          return null;
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [dataProvider]
  );

  const submitProduct = useCallback(
    async (
      organizationId: string,
      body: AddProductToSubscriptionRequest
    ): Promise<OrganizationSubscription | undefined> => {
      const result = await mutateAsync({
        url: `api/organizations/${organizationId}/subscriptions/products`,
        method: "post",
        values: body,
        successNotification: false,
        errorNotification: false,
      });
      return result.data;
    },
    [mutateAsync]
  );

  return { lookupSubscription, submitProduct, isLoading, isSubmitting };
}
